import { useState, useEffect, useContext, useCallback } from "react";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import Axios from "axios";

import {
  Label,
  Error,
  Description,
  Icon,
  Button,
  ViewContext,
  useNavigate,
  cn,
  useTranslation,
} from "components/lib";

import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

import Inputs from "./input/map";
import ReactFlagsSelect from "react-flags-select";
const StyledFlagsSelectWrapper = styled.div`
  .react-flag-select button {
    padding: 0;
    border: 0;
    font-size: 14px !important;
  }
`;
function Form({
  inputs,
  url,
  method,
  submitOnChange,
  callback,
  redirect,
  className,
  buttonText,
  destructive,
  cancel,
  card_metadata,
  ...props
}) {
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    trigger,
    unregister,
    watch,
    formState: { errors, touchedFields },
  } = useForm({ mode: "onBlur" });

  const viewContext = useContext(ViewContext);
  const [loading, setLoading] = useState(false);

  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  function onCardItemChange(name, value) {
    validateCardField(name, value);
    // update card values when the user selects a country
    setCardValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const [cardValues, setCardValues] = useState({
    name: "",
    email: "",
    address_line1: "",
    address_city: "",
    state: "",
    address_country: "",
    address_zip: "",
  });
  const [cardErrors, setCardErrors] = useState({
    name: null,
    email: null,
    address_line1: null,
    address_city: null,
    state: null,
    address_zip: null,
    address_country: null,
  });
  // init default values
  useEffect(() => {
    inputs &&
      Object.keys(inputs).length &&
      Object.keys(inputs).forEach((key) => {
        const input = inputs[key];

        if (!watch(key)) {
          setValue(key, input.defaultValue || input.value);
        }
      });
  }, [setValue, inputs]);

  // handle external inputs prop change
  useEffect(() => {
    inputs &&
      Object.keys(inputs).length &&
      Object.keys(inputs).forEach((key) => {
        // unregister input if it's hidden
        if (inputs[key].type === null) unregister(key);
      });
  }, [inputs]);

  // watch for changes
  useEffect(() => {
    const subscription = watch(async (data) => {
      // uncomment me to watch changes
      // console.log('watch', data);

      if (submitOnChange) handleSubmit(submit)();
    });

    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const onChange = useCallback(
    async (e, field) => {
      // internal handler
      field.onChange(e);

      // external handler to react to input changes
      if (props.onChange) {
        const valid = await trigger(field.name); // validate field
        props.onChange({
          input: field.name,
          value: e.target.value,
          valid: valid,
        });
      }
    },
    [props.onChange]
  );
  const validateCardField = (name, value) => {
    let error = null;

    // Required validation
    if (!value) {
      error = t("global.form.input.error.required", {
        label: t(`auth.signup.plan.form.${name}.label`),
      });
    }

    // Email specific validation
    if (
      name === "email" &&
      value &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      error = t("auth.signup.plan.form.email.error.invalid");
    }

    // ZIP code validation (basic)
    if (
      name === "address_zip" &&
      value &&
      !/^[A-Za-z0-9][A-Za-z0-9\- ]{0,10}[A-Za-z0-9]$/.test(value)
    ) {
      error = t("auth.signup.plan.form.address_zip.error.invalid");
    }

    setCardErrors((prev) => ({ ...prev, [name]: error }));
    return !error;
  };
  const submit = useCallback(
    async (data) => {
      // console.log('submit.data', data)
      if (card_metadata) {
        const cardValidations = Object.keys(cardValues).map((name) =>
          validateCardField(name, cardValues[name])
        );

        if (cardValidations.some((valid) => !valid)) {
          setLoading(false);
          return false;
        }
      }
      const valid = await trigger();
      // validate
      if (!valid) {
        setLoading(false);
        return false;
      }

      // no url - use callback instead of action
      if (!url) return callback?.(null, data);

      // start loading
      setLoading(true);

      try {
        let headers, payload;

        // generate a credit card token
        if (data.token) {
          const cardElement = props.elements.getElement(CardElement);
          const { token } = await props.stripe.createToken(
            cardElement,
            cardValues
          );

          if (!token) {
            throw new Error("Failed to create Stripe token");
          }

          data.token = token;
          if (cardValues.email) data.billing_email = cardValues.email;
        }
        // handle files
        if (Object.values(data).some((value) => value instanceof FileList)) {
          // prepare form data for file upload
          headers = { "Content-Type": "multipart/form-data" };
          payload = new FormData();

          Object.keys(data).forEach((key) => {
            data[key] instanceof FileList
              ? Array.from(data[key]).forEach((file) =>
                  payload.append(key, file)
                )
              : payload.append(key, data[key]);
          });
        } else {
          // prepare json payload for non file forms
          headers = { "Content-Type": "application/json" };
          payload = JSON.stringify(data);
        }
        debugger;
        // submit the form
        let res = await Axios({ method, url, data: payload, headers });

        // handle stripe callback?
        if (res.data.requires_payment_action) {
          res = await props.stripeCallback({
            formData: data,
            resData: res.data,
          });
        }

        setLoading(false);

        if (!res) return false; // end execution – error was handled

        // show message
        if (res.data.message)
          viewContext.notification({ description: res.data.message });

        // execute callback (send res and local data)
        callback?.(res, data);

        // redirect
        if (redirect) navigate(redirect);
      } catch (err) {
        // handle error
        setLoading(false);
        console.error(err);

        // input or generic error
        if (err.response?.data) {
          const input = err.response.data.inputError;
          const message = err.response.data.message;

          input
            ? setError(input, { type: "server", message: message })
            : viewContext.handleError(err);
        }
      }
    },
    [
      callback,
      viewContext.notification,
      navigate,
      trigger,
      url,
      method,
      redirect,
      props.stripeCallback,
      cardValues,
      card_metadata,
    ]
  );

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className={cn(loading && "opacity-50 pointer-events-none", className)}
      noValidate
    >
      {card_metadata && (
        <div className="">
          <h2 className="text-[14px] tracking-[0.16px] font-medium text-[#02172C] mb-3">
            {t("auth.signup.plan.form.billing_info.title")}
          </h2>
          <div className="border border-[#F2F2F2] rounded-[7px] py-2 px-5 mb-4">
            {/* Name */}
            <div className="mb-4 border-bottom border-[#F2F2F2] flex gap-4">
              <div className="text-right w-3/12">
                <Label className="mb-0 text-[12px] text-[#02172C] font-[400]">
                  {t("auth.signup.plan.form.name.label")}
                </Label>
              </div>
              <div className="w-9/12">
                <input
                  type="text"
                  className="placeholder:text-[14px] focus:outline-none h-auto px-2 p-0 bg-transparent text-[14px] text-[#02172C] placeholder:text-[#8D9299] rounded-[0px] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-1 w-full"
                  placeholder={t("auth.signup.plan.form.name.placeholder")}
                  defaultValue={""}
                  onChange={(e) => onCardItemChange("name", e.target.value)}
                  onBlur={(e) => onCardItemChange("name", e.target.value)}
                />
                {cardErrors.name && (
                  <div className="text-red-500 text-xs mt-1">
                    {cardErrors.name}
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="mb-4 border-bottom border-[#F2F2F2] flex gap-4">
              <div className="text-right w-3/12">
                <Label className="mb-0 text-[12px] text-[#02172C] font-[400]">
                  {t("auth.signup.plan.form.email.label")}
                </Label>
              </div>
              <div className="w-9/12">
                <input
                  type="email"
                  className="placeholder:text-[14px] focus:outline-none h-auto px-2 p-0 bg-transparent text-[14px] text-[#02172C] placeholder:text-[#8D9299] rounded-[0px] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-1 w-full"
                  placeholder={t("auth.signup.plan.form.email.placeholder")}
                  defaultValue={""}
                  onChange={(e) => onCardItemChange("email", e.target.value)}
                  onBlur={(e) => onCardItemChange("email", e.target.value)}
                />
                {cardErrors.email && (
                  <div className="text-red-500 text-xs mt-1">
                    {cardErrors.email}
                  </div>
                )}
              </div>
            </div>

            {/* Address Line 1 */}
            <div className="mb-4 border-bottom border-[#F2F2F2] flex gap-4">
              <div className="text-right w-3/12">
                <Label className="mb-0 text-[12px] text-[#02172C] font-[400]">
                  {t("auth.signup.plan.form.address_line1.label")}
                </Label>
              </div>
              <div className="w-9/12">
                <input
                  type="text"
                  className="placeholder:text-[14px] focus:outline-none h-auto px-2 p-0 bg-transparent text-[14px] text-[#02172C] placeholder:text-[#8D9299] rounded-[0px] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-1 w-full"
                  placeholder={t(
                    "auth.signup.plan.form.address_line1.placeholder"
                  )}
                  defaultValue={""}
                  onChange={(e) =>
                    onCardItemChange("address_line1", e.target.value)
                  }
                  onBlur={(e) =>
                    onCardItemChange("address_line1", e.target.value)
                  }
                />
                {cardErrors.address_line1 && (
                  <div className="text-red-500 text-xs mt-1">
                    {cardErrors.address_line1}
                  </div>
                )}
              </div>
            </div>

            {/* City */}
            <div className="mb-4 border-bottom border-[#F2F2F2] flex gap-4">
              <div className="text-right w-3/12">
                <Label className="mb-0 text-[12px] text-[#02172C] font-[400]">
                  {t("auth.signup.plan.form.address_city.label")}
                </Label>
              </div>
              <div className="w-9/12">
                <input
                  type="text"
                  className="placeholder:text-[14px] focus:outline-none h-auto px-2 p-0 bg-transparent text-[14px] text-[#02172C] placeholder:text-[#8D9299] rounded-[0px] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-1 w-full"
                  placeholder={t(
                    "auth.signup.plan.form.address_city.placeholder"
                  )}
                  defaultValue={""}
                  onChange={(e) =>
                    onCardItemChange("address_city", e.target.value)
                  }
                  onBlur={(e) =>
                    onCardItemChange("address_city", e.target.value)
                  }
                />
                {cardErrors.address_city && (
                  <div className="text-red-500 text-xs mt-1">
                    {cardErrors.address_city}
                  </div>
                )}
              </div>
            </div>

            {/* State */}
            <div className="mb-4 border-bottom border-[#F2F2F2] flex gap-4">
              <div className="text-right w-3/12">
                <Label className="mb-0 text-[12px] text-[#02172C] font-[400]">
                  {t("auth.signup.plan.form.state.label")}
                </Label>
              </div>
              <div className="w-9/12">
                <input
                  type="text"
                  className="placeholder:text-[14px] focus:outline-none h-auto px-2 p-0 bg-transparent text-[14px] text-[#02172C] placeholder:text-[#8D9299] rounded-[0px] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-1 w-full"
                  placeholder={t("auth.signup.plan.form.state.placeholder")}
                  defaultValue={""}
                  onChange={(e) => onCardItemChange("state", e.target.value)}
                  onBlur={(e) => onCardItemChange("state", e.target.value)}
                />
                {cardErrors.state && (
                  <div className="text-red-500 text-xs mt-1">
                    {cardErrors.state}
                  </div>
                )}
              </div>
            </div>

            {/* ZIP */}
            <div className="mb-4 border-bottom border-[#F2F2F2] flex gap-4">
              <div className="text-right w-3/12">
                <Label className="mb-0 text-[12px] text-[#02172C] font-[400]">
                  {t("auth.signup.plan.form.address_zip.label")}
                </Label>
              </div>
              <div className="w-9/12">
                <input
                  type="text"
                  className="placeholder:text-[14px] focus:outline-none h-auto px-2 p-0 bg-transparent text-[14px] text-[#02172C] placeholder:text-[#8D9299] rounded-[0px] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-1 w-full"
                  placeholder={t(
                    "auth.signup.plan.form.address_zip.placeholder"
                  )}
                  defaultValue={""}
                  onChange={(e) =>
                    onCardItemChange("address_zip", e.target.value)
                  }
                  onBlur={(e) =>
                    onCardItemChange("address_zip", e.target.value)
                  }
                />
                {cardErrors.address_zip && (
                  <div className="text-red-500 text-xs mt-1">
                    {cardErrors.address_zip}
                  </div>
                )}
              </div>
            </div>

            {/* Country */}
            <div className="mb-4 flex gap-4">
              <div className="text-right w-3/12">
                <Label className="mb-0 text-[12px] text-[#02172C] font-[400]">
                  {t("auth.signup.plan.form.address_country.label")}
                </Label>
              </div>
              <div className="w-9/12">
                <StyledFlagsSelectWrapper>
                  <ReactFlagsSelect
                    selected={cardValues.address_country || "US"}
                    onSelect={(code) =>
                      onCardItemChange("address_country", code)
                    }
                    searchable
                    fullWidth
                    className={`react-flag-select ${
                      cardErrors.address_country
                        ? "border-b border-red-500"
                        : ""
                    }`}
                    optionsSize={14}
                  />
                </StyledFlagsSelectWrapper>
                {cardErrors.address_country && (
                  <div className="text-red-500 text-xs mt-1">
                    {cardErrors.address_country}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {inputs &&
        Object.keys(inputs).length &&
        Object.keys(inputs).map((name) => {
          const {
            type,
            required,
            label,
            errorMessage,
            min,
            max,
            minLength,
            maxLength,
            placeholder,
            description,
            value,
            defaultValue,
            options,
            disabled,
            validation,
            accept,
            readonly,
            className,
            icon,
          } = inputs[name];

          // ignore null input type
          if (inputs[name].type === null) return false;

          // input type to render or default generic input
          // imported from ./input/map.js file
          const Input = Inputs[type] ? Inputs[type] : Inputs.default;

          return (
            <div
              className={cn(type !== "hidden" && "mb-4 last:mb-0")}
              key={name}
            >
              {label && Input.showLabel && type !== "checkbox" && type !== "text" && (
                <Label
                  htmlFor={name}
                  required={required}
                  className="flex gap-3 items-center"
                >
                  {icon && (
                    <img src={icon} alt="icon" className="w-[16px] h-[16px]" />
                  )}
                  {label}
                </Label>
              )}

              <div className="relative">
                <Controller
                  name={name}
                  control={control}
                  defaultValue={value || defaultValue || ""}
                  rules={{
                    // validation
                    required: required
                      ? errorMessage ||
                        t("global.form.input.error.required", {
                          label: label || "Field",
                        })
                      : undefined,

                    min: min
                      ? {
                          value: min,
                          message:
                            errorMessage ||
                            t("global.form.input.error.min", { min }),
                        }
                      : undefined,

                    max: max
                      ? {
                          value: max,
                          message:
                            errorMessage ||
                            t("global.form.input.error.max", { label, max }),
                        }
                      : undefined,

                    minLength: minLength
                      ? {
                          value: minLength,
                          message:
                            errorMessage ||
                            t("global.form.input.error.min_length", {
                              label,
                              minLength,
                            }),
                        }
                      : undefined,

                    maxLength: maxLength
                      ? {
                          value: maxLength,
                          message:
                            errorMessage ||
                            t("global.form.input.error.max_length", {
                              label,
                              maxLength,
                            }),
                        }
                      : undefined,

                    // input specific default
                    ...(Input.validation
                      ? {
                          pattern: {
                            value: Input.validation.default,
                            message:
                              errorMessage ||
                              t(`global.form.${type}.error.invalid`, {
                                label,
                                type,
                              }),
                          },
                        }
                      : {}),

                    // input specific passed as prop
                    ...Object.keys(validation || {}).reduce((acc, key) => {
                      if (validation[key] === true && Input.validation?.[key]) {
                        acc.pattern = {
                          value: Input.validation[key],
                          message:
                            errorMessage ||
                            t(`global.form.${type}.error.invalid`, {
                              label,
                              type,
                            }),
                        };
                      }

                      return acc;
                    }, {}),
                  }}
                  render={({ field }) => (
                    <>
                      <Input.component
                        {...field}
                        id={name}
                        options={options}
                        label={label}
                        type={
                          type === "password" && passwordVisible ? "text" : type
                        }
                        accept={accept}
                        readOnly={readonly}
                        disabled={disabled}
                        placeholder={placeholder}
                        onChange={(e) => onChange(e, field)}
                        defaultChecked={defaultValue}
                        defaultValue={defaultValue}
                        value={field.value}
                        aria-describedby={
                          errors[name]
                            ? `${name}-helper ${name}-error`
                            : `${name}-helper`
                        }
                        aria-invalid={!!errors[name]}
                      />
                      {type === "password" && (
                        <Icon
                          name={passwordVisible ? "eye" : "eye-off"}
                          onClick={() => {
                            setPasswordVisible((prev) => !prev);
                          }}
                          className="absolute right-6 top-1/2 transform -translate-y-1/2 cursor-pointer z-9 icon"
                          size={20}
                        />
                      )}
                    </>
                  )}
                />

                {/* success/error icon */}
                {touchedFields[name] && Input.showIcon && (
                  <Icon
                    size={14}
                    className="absolute top-1/2 transform -translate-y-1/2 right-2"
                    name={errors[name] ? "x" : "check"}
                    color={errors[name] ? "red" : "green"}
                  />
                )}
              </div>

              {/* error message */}
              {errors[name] && type != "hidden" && (
                <Error id={`${name}-error`}>{errors[name].message}</Error>
              )}

              {/* description helper */}
              {description && !errors[name] && (
                <Description id={`${name}-helper`}>{description}</Description>
              )}
            </div>
          );
        })}

      {/* submit button */}
      {buttonText && (
        <Button
          type="submit"
          loading={loading}
          text={buttonText}
          color={destructive ? "red" : "primary"}
          className="w-[49%] mr-[2%] last:mr-0 text-white"
          size={cancel ? "default" : "full"}
        />
      )}

      {/* cancel button */}
      {cancel && (
        <Button
          variant="outline"
          text="Cancel"
          className="w-[49%] mr-[2%] last:mr-0"
          action={cancel}
        />
      )}
    </form>
  );
}

function PaymentForm(props) {
  const stripe = useStripe();
  const elements = useElements();
  const viewContext = useContext(ViewContext);

  async function handleStripeCallback({ formData, resData }) {
    debugger;
    // check for 2-factor payment requirement
    if (resData.requires_payment_action) {
      try {
        const { error } = await stripe.handleCardPayment(resData.client_secret);
        if (error) throw error;

        return await Axios({
          method: props.method,
          url: props.url,
          data: { ...formData, ...{ stripe: resData } },
        });
      } catch (err) {
        viewContext.handleError(err);
      }
    }
  }

  return (
    <Form
      {...props}
      stripe={stripe}
      card_metadata={props.card_metadata}
      elements={elements}
      stripeCallback={handleStripeCallback}
      className="py-4 px-5"
    />
  );
}

export { Form, PaymentForm };
